/**
 * In-app confirmation dialog. The browser's confirm() can't be used: the Tauri
 * dialog plugin replaces it with an async version whose native command doesn't
 * exist, so on desktop it either fails or (unawaited) reads as "yes".
 * ConfirmDialog.vue, mounted in App.vue and AppMobile.vue, renders the request.
 */
import { shallowRef } from 'vue'

export interface ConfirmOptions {
  title: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Styles the confirm button as destructive. */
  danger?: boolean
}

export interface ConfirmRequest extends ConfirmOptions {
  resolve: (confirmed: boolean) => void
}

export const confirmRequest = shallowRef<ConfirmRequest | null>(null)

/** Set by ConfirmDialog.vue while it is mounted. */
export const confirmHost = { mounted: false }

export function askConfirm(options: ConfirmOptions): Promise<boolean> {
  if (!confirmHost.mounted) {
    // No app shell (e.g. a test); a plain browser still has a working confirm().
    return Promise.resolve(window.confirm([options.title, options.message].filter(Boolean).join('\n\n')))
  }
  // A new question replaces an unanswered one, which counts as canceled.
  confirmRequest.value?.resolve(false)
  return new Promise((resolve) => {
    confirmRequest.value = { ...options, resolve }
  })
}

export function answerConfirm(confirmed: boolean): void {
  const request = confirmRequest.value
  if (!request) return
  confirmRequest.value = null
  request.resolve(confirmed)
}
